import { ReactNode } from "react";

import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  PanelRight,
  PanelRightClose,
  SquareArrowDownLeft,
  X,
} from "lucide-react";

import { Button } from "@components/ui/button";

import { cn } from "@utils/ui";

export type FloatingPanelMode = "floating" | "docked" | "popout";

type PanelHeaderProps = {
  title: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  mode: FloatingPanelMode;
  isMinimised: boolean;
  onToggleMinimised: () => void;
  onToggleDocked: () => void;
  onPopout: () => void;
  onBringBack: () => void;
  onClose: () => void;
  onDragStart?: (e: React.PointerEvent) => void;
};

const IconButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) => (
  <Button
    variant="ghost"
    size="icon"
    className="size-8 rounded-md text-foreground-muted"
    aria-label={label}
    title={label}
    onClick={onClick}
  >
    {children}
  </Button>
);

export const PanelHeader = ({
  title,
  subtitle,
  icon,
  mode,
  isMinimised,
  onToggleMinimised,
  onToggleDocked,
  onPopout,
  onBringBack,
  onClose,
  onDragStart,
}: PanelHeaderProps) => (
  <div
    data-testid="floating-panel-drag-handle"
    onPointerDown={mode === "floating" ? onDragStart : undefined}
    className={cn(
      "flex items-start gap-3 border-b border-border px-5 py-4",
      mode === "floating" &&
        "cursor-grab touch-none select-none active:cursor-grabbing"
    )}
  >
    {icon && (
      <span className="mt-1 shrink-0 text-secondary [&_svg]:size-4" aria-hidden>
        {icon}
      </span>
    )}
    <div className="min-w-0 flex-1">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {subtitle && (
        <div className="mt-0.5 text-xs text-foreground-muted">{subtitle}</div>
      )}
    </div>
    <div className="flex shrink-0 items-center gap-1">
      {mode === "popout" ? (
        <IconButton label="Bring back into app" onClick={onBringBack}>
          <SquareArrowDownLeft />
        </IconButton>
      ) : (
        <>
          <IconButton
            label={isMinimised ? "Restore" : "Minimise"}
            onClick={onToggleMinimised}
          >
            {isMinimised ? <ChevronDown /> : <ChevronUp />}
          </IconButton>
          <IconButton
            label={mode === "docked" ? "Undock" : "Dock to side"}
            onClick={onToggleDocked}
          >
            {mode === "docked" ? <PanelRightClose /> : <PanelRight />}
          </IconButton>
          <IconButton label="Open in separate window" onClick={onPopout}>
            <ExternalLink />
          </IconButton>
        </>
      )}
      <IconButton label="Close" onClick={onClose}>
        <X />
      </IconButton>
    </div>
  </div>
);
